/**
 * BottomNav - Mobile bottom navigation bar.
 *
 * Fixed to bottom of screen on mobile (<768px), hidden on desktop.
 * 5 items: Home, Browse, Post, My Listings, Profile.
 * Active item highlighted with primary color.
 * Minimum 48px touch targets for LIFF compatibility.
 *
 * Requirements: 22.1, 22.4, 22.6, 25.3, 25.4
 */

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth, redirectToLineOAuth } from '@/contexts/AuthContext';
import apiClient from '@/services/api';

interface NavItem {
  path: string;
  labelKey: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  {
    path: '/',
    labelKey: 'nav.home',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
      </svg>
    ),
  },
  {
    path: '/search',
    labelKey: 'nav.search',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
  },
  {
    path: '/posts/new',
    labelKey: 'nav.post',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
      </svg>
    ),
  },
  {
    path: '/saved',
    labelKey: 'nav.saved',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
      </svg>
    ),
  },
  {
    path: '/notifications',
    labelKey: 'nav.notifications',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
      </svg>
    ),
  },
  {
    path: '/profile',
    labelKey: 'nav.profile',
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    ),
  },
];

export function BottomNav() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  // Poll unread notification count for badge
  const fetchUnreadCount = useCallback(async () => {
    if (!user) { setUnreadCount(0); return; }
    try {
      const res = await apiClient.get<{ count: number }>('/notifications/unread-count');
      setUnreadCount(res.data.count ?? (res.data as any).unreadCount ?? 0);
    } catch { /* silent */ }
  }, [user]);

  useEffect(() => {
    fetchUnreadCount();
    const id = setInterval(fetchUnreadCount, 60_000);
    return () => clearInterval(id);
  }, [fetchUnreadCount]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/search') return location.pathname.startsWith('/search') || location.pathname.startsWith('/browse');
    return location.pathname.startsWith(path);
  };

  // Replace the profile nav item with login button when not authenticated
  const profileItem = navItems[navItems.length - 1];
  if (!profileItem) return null;

  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white md:hidden"
      aria-label="Mobile navigation"
    >
      <ul className="flex items-center justify-around">
        {navItems.slice(0, -1).map((item) => {
          const active = isActive(item.path);
          const isNotif = item.path === '/notifications';
          return (
            <li key={item.path}>
              <NavLink
                to={item.path}
                className={`flex min-h-[48px] min-w-[48px] flex-col items-center justify-center px-2 py-1 text-xs transition-colors ${
                  active
                    ? 'text-primary-600 font-medium'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                aria-current={active ? 'page' : undefined}
              >
                <span className={`relative ${active ? 'text-primary-600' : 'text-gray-400'}`}>
                  {item.icon}
                  {isNotif && unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-0.5 text-[9px] font-bold text-white">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </span>
                <span className="mt-0.5">{t(item.labelKey)}</span>
              </NavLink>
            </li>
          );
        })}
        {/* Last item: Profile (if logged in) or Login (if not) */}
        <li>
          {user ? (
            <NavLink
              to={profileItem.path}
              className={`flex min-h-[48px] min-w-[48px] flex-col items-center justify-center px-2 py-1 text-xs transition-colors ${
                isActive(profileItem.path)
                  ? 'text-primary-600 font-medium'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              aria-current={isActive(profileItem.path) ? 'page' : undefined}
            >
              <span className={isActive(profileItem.path) ? 'text-primary-600' : 'text-gray-400'}>
                {profileItem.icon}
              </span>
              <span className="mt-0.5">{t(profileItem.labelKey)}</span>
            </NavLink>
          ) : (
            <button
              type="button"
              onClick={() => redirectToLineOAuth()}
              className="flex min-h-[48px] min-w-[48px] flex-col items-center justify-center px-2 py-1 text-xs text-[#06C755] font-medium transition-colors"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
              </svg>
              <span className="mt-0.5">{t('auth.login')}</span>
            </button>
          )}
        </li>
      </ul>
    </nav>
  );
}
